import React from "react";
import { Link } from "react-router-dom";
import { CBadge, CTooltip } from "@coreui/react";
import type { ApiJob } from "@tradingdroid/frontends-types";

import { getCoordinatorProgress } from "@src/utils/jobChildren";

interface Props {
  job: ApiJob;
}

/**
 * Compact child count for a coordinator job, e.g. `+200C`. Renders nothing for a job that reports
 * no children. Links to the job detail page, where the children are listed.
 */
export const JobChildrenBadge: React.FC<Props> = ({ job }) => {
  const progress = getCoordinatorProgress(job);
  if (!progress) return null;

  // The counts alone can read as a finished experiment on a run that lost units: a Monte Carlo
  // child that completes without contributing a usable value still counts as completed, so the
  // ratio reaches its total while the aggregate covers fewer. The run's own marker is what says so.
  const tooltip =
    `${progress.done} of ${progress.total} ${progress.unitLabel} finished` +
    (progress.failed > 0 ? `, ${progress.failed} failed` : "") +
    (progress.partial
      ? `. Partial: the numbers cover less than the planned experiment` +
        (progress.stopReason ? ` (${progress.stopReason})` : "")
      : "") +
    ". Open the job to list them.";

  return (
    <CTooltip content={tooltip} placement="top">
      <Link to={`/job/${job.id}`} className="JobChildrenBadge">
        <CBadge color={progress.failed > 0 ? "danger" : "secondary"} shape="rounded-pill">
          +{progress.total}C
        </CBadge>
        {progress.partial && (
          <CBadge className="JobChildrenPartialBadge ms-1" color="warning" shape="rounded-pill">
            Partial
          </CBadge>
        )}
      </Link>
    </CTooltip>
  );
};
